import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../api";
import { setToken } from "../App";
import { toast } from "sonner";
import { Sparkles, ArrowRight } from "lucide-react";

export default function Login() {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    if (!password.trim()) return;
    setLoading(true);
    try {
      const res = await api.post("/auth/verify", { password });
      setToken(res.data.token);
      toast.success("Bienvenue !");
      navigate("/");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Mot de passe incorrect");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen paper-bg relative overflow-hidden"
      data-testid="login-page"
    >
      {/* Marquee top */}
      <div className="border-b-[1.5px] border-[var(--ink)] py-2 bg-[var(--ink)] text-[var(--bg)] relative z-10">
        <div className="marquee">
          <div className="marquee-track font-ui text-[11px] uppercase tracking-[0.3em]">
            {Array.from({ length: 2 }).map((_, j) => (
              <div key={j} className="flex items-center gap-12 pr-12">
                <span>★ archives de famille</span>
                <span className="font-hebrew text-base normal-case tracking-normal">פרקי פרלה</span>
                <span>★ vol. 01</span>
                <span>★ levy · perla · taly · linou</span>
                <span>★ ne perds plus jamais une perle</span>
                <span className="font-hebrew text-base normal-case tracking-normal">פרקי פרלה</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Floating decor */}
      <Sparkles className="absolute top-24 right-[8%] text-[var(--kid-perla)] sparkle z-0" size={28} />
      <Sparkles className="absolute bottom-24 left-[6%] text-[var(--kid-levy)] sparkle z-0" size={22} style={{ animationDelay: "1.2s" }} />
      <div className="absolute top-1/3 left-[3%] w-3 h-3 rounded-full bg-[var(--kid-taly)] sparkle z-0" style={{ animationDelay: "0.6s" }} />
      <div className="absolute bottom-1/4 right-[12%] w-3 h-3 rounded-full bg-[var(--kid-linou)] sparkle z-0" style={{ animationDelay: "1.8s" }} />

      <div className="relative z-10 px-4 py-12 lg:py-16">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-12 gap-10 items-center">
          {/* Left — hero polaroid */}
          <div className="relative hidden lg:block lg:col-span-6">
            {/* Back layer polaroid (pink) */}
            <div
              className="absolute -top-4 -left-6 bg-[var(--accent-rose)] border-[1.5px] border-[var(--ink)] w-[88%] h-[440px] rotate-[4deg]"
              style={{ boxShadow: "6px 6px 0 0 #1c1b1a" }}
            />
            {/* Front polaroid */}
            <div
              className="relative bg-white border-[1.5px] border-[var(--ink)] p-4 pb-16 rotate-[-3deg] float-slow"
              style={{ boxShadow: "10px 10px 0 0 #1c1b1a" }}
            >
              <div className="tape" />
              <img
                src="https://images.unsplash.com/photo-1632757653756-7d8df6a72e76?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwzfHxraWRzJTIwcGxheWluZyUyMHZpbnRhZ2UlMjBwb2xhcm9pZHxlbnwwfHx8fDE3NzY5MTgyOTF8MA&ixlib=rb-4.1.0&q=85"
                alt="enfance"
                className="w-full h-[420px] object-cover"
              />
              <div className="absolute bottom-3 left-0 right-0 text-center font-handwrite text-3xl text-[var(--ink)]">
                pirkei perla &amp; co
              </div>
            </div>

            <div
              className="absolute -bottom-6 -right-2 bg-[var(--kid-perla)] border-[1.5px] border-[var(--ink)] px-4 py-2 rotate-[6deg] font-ui text-[11px] uppercase tracking-[0.28em] z-10"
              style={{ boxShadow: "4px 4px 0 0 #1c1b1a" }}
            >
              4 enfants · 1 carnet
            </div>

            <div className="absolute -top-10 -right-4 wobble-slow z-10">
              <div className="stamp bg-[var(--bg)]">
                est. 2026
              </div>
            </div>
          </div>

          {/* Right — form */}
          <div className="lg:col-span-6 max-w-xl">
            <div className="flex items-center gap-3 mb-3">
              <span className="dot-perla" />
              <span className="font-ui text-[11px] uppercase tracking-[0.32em] text-[var(--ink-soft)]">
                Vol. 01 · Édition familiale
              </span>
            </div>

            <h1 className="font-serif-display text-[3.5rem] sm:text-7xl lg:text-[5.5rem] leading-[0.95] mb-3">
              Pirkei
              <br />
              <span className="text-[var(--kid-levy)]">Perla</span>
              <span className="font-ui not-italic text-3xl sm:text-5xl lg:text-6xl tracking-tight"> &amp; Co.</span>
            </h1>

            <div className="font-hebrew text-3xl sm:text-4xl text-[var(--ink-soft)] mb-6" dir="rtl">
              פִּרְקֵי פֶּרְלָה
            </div>

            <p className="font-ui text-base sm:text-lg text-[var(--ink-soft)] leading-relaxed mb-12 max-w-md">
              Le carnet sacré des perles de <span className="font-handwrite text-2xl text-[var(--kid-levy)]">Levy</span>,
              {" "}<span className="font-handwrite text-2xl text-[var(--kid-perla)]">Perla</span>,
              {" "}<span className="font-handwrite text-2xl text-[var(--kid-taly)]">Taly</span> &amp;
              {" "}<span className="font-handwrite text-2xl text-[var(--kid-linou)]">Linou</span>.
              <br />
              Tous les jours ils sortent des trucs qui éclatent — on n'oublie plus rien.
            </p>

            <form onSubmit={submit} data-testid="login-form" className="space-y-8 max-w-md">
              <div>
                <label className="font-ui text-[11px] uppercase tracking-[0.3em] text-[var(--ink-soft)] block mb-2">
                  → mot de passe famille
                </label>
                <input
                  data-testid="login-password-input"
                  type="password"
                  className="input-line"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoFocus
                />
              </div>

              <button
                type="submit"
                className="btn-ink inline-flex items-center gap-2 text-base"
                disabled={loading}
                data-testid="login-submit-button"
              >
                {loading ? "ouverture…" : (
                  <>
                    ouvrir le carnet
                    <ArrowRight size={16} />
                  </>
                )}
              </button>
            </form>

            <div
              className="mt-16 pl-6 border-l-2 border-[var(--ink)] max-w-md"
            >
              <div className="font-serif-display text-2xl italic text-[var(--ink)] leading-snug">
                « Maman, t'sais que les étoiles<br />c'est juste des trous dans la nuit ? »
              </div>
              <div className="mt-2 font-ui not-italic text-[11px] uppercase tracking-[0.3em] text-[var(--ink-soft)]">
                — une perle de la maison, mardi
              </div>
            </div>
          </div>
        </div>

        {/* Bottom strip */}
        <div className="max-w-6xl mx-auto mt-20 pt-6 border-t border-dashed border-[var(--ink)]/40 flex items-center justify-between font-ui text-[11px] uppercase tracking-[0.28em] text-[var(--ink-soft)]">
          <span>p. 01</span>
          <span>tirage limité — usage familial</span>
          <span>n° {new Date().getFullYear()}</span>
        </div>
      </div>
    </div>
  );
}
